import { useCallback, useEffect, useState } from 'react';
import type { EstadoReferido, Referido, TipoComision, UsuarioSesion } from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Icono } from '../componentes/Iconos.js';

interface Props {
  usuario: UsuarioSesion;
  idioma: Idioma;
  onVolver: () => void;
}

const ESTADOS = ['TODOS', 'PENDIENTE', 'CONVERTIDO', 'DESCARTADO'] as const;
const CLASE_ESTADO: Record<EstadoReferido, string> = {
  PENDIENTE: 'AMARILLO',
  CONVERTIDO: 'VERDE',
  DESCARTADO: 'ROJO',
};

/**
 * Referidos y comisiones. Alguien refiere un negocio; al convertirse, se calcula
 * su comisión (porcentaje sobre una base, o importe fijo) y se congela. El
 * cálculo lo hace el servidor.
 */
export function Referidos({ usuario, idioma, onVolver }: Props) {
  const [lista, setLista] = useState<Referido[] | null>(null);
  const [filtro, setFiltro] = useState<EstadoReferido | 'TODOS'>('TODOS');
  const [error, setError] = useState<string | null>(null);

  // Alta
  const [nombre, setNombre] = useState('');
  const [tipo, setTipo] = useState<TipoComision>('PORCENTAJE');
  const [valor, setValor] = useState('');

  // Conversión: base por referido
  const [bases, setBases] = useState<Record<string, string>>({});

  const cargar = useCallback(() => {
    setError(null);
    api
      .referidos(filtro === 'TODOS' ? undefined : filtro)
      .then(({ referidos }) => setLista(referidos))
      .catch((e: unknown) =>
        setError(e instanceof ErrorApi ? e.message : t(idioma, 'ref.noSeCargaron')),
      );
  }, [filtro, idioma]);
  useEffect(cargar, [cargar]);

  async function crear() {
    if (nombre.trim().length < 2) return;
    const v = Number(valor.replace(',', '.'));
    // Porcentaje → puntos básicos; fijo → céntimos.
    const comisionValor = tipo === 'PORCENTAJE' ? Math.round(v * 100) : Math.round(v * 100);
    try {
      await api.crearReferido({
        referidorUserId: usuario.id,
        nombreReferido: nombre.trim(),
        comisionTipo: tipo,
        ...(Number.isFinite(v) && v > 0 ? { comisionValor } : {}),
      });
      setNombre('');
      setValor('');
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : t(idioma, 'ref.noSeCreo'));
    }
  }

  async function convertir(ref: Referido) {
    const base = Number((bases[ref.id] ?? '').replace(',', '.'));
    try {
      await api.convertirReferido(ref.id, {
        ...(ref.comisionTipo === 'PORCENTAJE' || (Number.isFinite(base) && base > 0)
          ? { baseCents: Math.round((Number.isFinite(base) ? base : 0) * 100) }
          : {}),
      });
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : t(idioma, 'ref.noSeConvirtio'));
    }
  }

  async function descartar(ref: Referido) {
    try {
      await api.descartarReferido(ref.id, t(idioma, 'ref.motivoDescarte'));
      cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : t(idioma, 'ref.noSeDescarto'));
    }
  }

  const comision = (ref: Referido) => {
    if (ref.comisionCents != null)
      return formatearImporteDesdeCents(ref.comisionCents, idioma, { moneda: ref.moneda });
    return ref.comisionTipo === 'PORCENTAJE'
      ? `${ref.comisionValor / 100} %`
      : formatearImporteDesdeCents(ref.comisionValor, idioma, { moneda: ref.moneda });
  };

  return (
    <main>
      <div className="cabecera-seccion">
        <div>
          <h1>{t(idioma, 'ref.titulo')}</h1>
          <p>{t(idioma, 'ref.sub')}</p>
        </div>
        <div className="acciones">
          <button type="button" className="secundario" onClick={onVolver}>
            <Icono nombre="volver" /> {t(idioma, 'app.volver')}
          </button>
        </div>
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      <div className="tarjeta crm-alta">
        <div className="campo">
          <label htmlFor="ref-nombre">{t(idioma, 'ref.nuevo')}</label>
          <input
            id="ref-nombre"
            value={nombre}
            placeholder={t(idioma, 'ref.nombrePlaceholder')}
            onChange={(e) => setNombre(e.target.value)}
          />
        </div>
        <div className="campo">
          <label htmlFor="ref-tipo">{t(idioma, 'ref.comision')}</label>
          <select id="ref-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as TipoComision)}>
            <option value="PORCENTAJE">{t(idioma, 'ref.porcentaje')}</option>
            <option value="FIJO">{t(idioma, 'ref.fija')}</option>
          </select>
        </div>
        <div className="campo">
          <label htmlFor="ref-valor">
            {t(idioma, tipo === 'PORCENTAJE' ? 'ref.porcentajeCampo' : 'ref.importeCampo')}
          </label>
          <input
            id="ref-valor"
            inputMode="decimal"
            value={valor}
            placeholder="0"
            onChange={(e) => setValor(e.target.value)}
          />
        </div>
        <button type="button" onClick={() => void crear()}>
          {t(idioma, 'ref.anadir')}
        </button>
      </div>

      <div className="crm-embudos">
        {ESTADOS.map((clave) => (
          <button
            key={clave}
            type="button"
            className={clave === filtro ? '' : 'secundario'}
            onClick={() => setFiltro(clave)}
          >
            {t(idioma, `ref.filtro${clave}`)}
          </button>
        ))}
      </div>

      {lista === null && !error && <p>{t(idioma, 'app.cargando')}</p>}
      {lista !== null && lista.length === 0 && (
        <div className="tarjeta">
          <p>{t(idioma, 'ref.vacio')}</p>
        </div>
      )}

      {lista !== null && lista.length > 0 && (
        <div className="tarjeta tabla-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">{t(idioma, 'ref.colReferido')}</th>
                <th scope="col">{t(idioma, 'ref.colQuienRefiere')}</th>
                <th scope="col">{t(idioma, 'ref.colEstado')}</th>
                <th scope="col">{t(idioma, 'ref.comision')}</th>
                <th scope="col">{t(idioma, 'ref.colAcciones')}</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((ref) => (
                <tr key={ref.id}>
                  <td>{ref.nombreReferido}</td>
                  <td>{ref.referidorNombre ?? '—'}</td>
                  <td>
                    <span className={`pastilla ${CLASE_ESTADO[ref.estado]}`}>
                      {t(idioma, `ref.estado${ref.estado}`)}
                    </span>
                  </td>
                  <td className="fin-num">{comision(ref)}</td>
                  <td>
                    {ref.estado === 'PENDIENTE' ? (
                      <div className="ref-acciones">
                        {ref.comisionTipo === 'PORCENTAJE' && (
                          <input
                            className="ref-base"
                            inputMode="decimal"
                            value={bases[ref.id] ?? ''}
                            placeholder={t(idioma, 'ref.basePlaceholder')}
                            aria-label={t(idioma, 'ref.baseDe', { nombre: ref.nombreReferido })}
                            onChange={(e) => setBases((b) => ({ ...b, [ref.id]: e.target.value }))}
                          />
                        )}
                        <button type="button" onClick={() => void convertir(ref)}>
                          {t(idioma, 'ref.convertir')}
                        </button>
                        <button type="button" className="secundario" onClick={() => void descartar(ref)}>
                          {t(idioma, 'ref.descartar')}
                        </button>
                      </div>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </main>
  );
}
